/**
 * Shared room utilities for class-related components
 * Consolidates room visibility logic across:
 * - ClassCard
 * - BrowseClassCard
 * - PhotoClassCard
 * - ClassMetadata
 */

/**
 * Determine if the room should be shown based on paid-only restrictions
 *
 * Room visibility rules:
 * - If room is not set, don't show
 * - If isStudioManaged is true, always show (studio-managed classes always display their room)
 * - If roomPaidOnly is false, always show
 * - If roomPaidOnly is true:
 *   - Show if user has paid
 *   - Show if user can manage the class (owner/collaborator)
 *   - Otherwise, hide
 *
 * @param room - The room name/value
 * @param roomPaidOnly - Whether room is restricted to paid users
 * @param isPaid - Whether the current user has paid
 * @param canManage - Whether the current user can manage the class
 * @param isStudioManaged - Whether this is a studio-managed class
 * @returns Whether the room should be shown
 */
export function shouldShowRoom(
  room: string | null | undefined,
  roomPaidOnly: boolean,
  isPaid = false,
  canManage = false,
  isStudioManaged = false
): boolean {
  if (!room) return false;
  if (isStudioManaged) return true;
  if (!roomPaidOnly) return true;
  return isPaid || canManage;
}

/**
 * Get the room display value, respecting paid-only restrictions
 * Returns the room name if visible, undefined otherwise
 *
 * @param room - The room name/value
 * @param roomPaidOnly - Whether room is restricted to paid users
 * @param isPaid - Whether the current user has paid
 * @param canManage - Whether the current user can manage the class
 * @param isStudioManaged - Whether this is a studio-managed class
 * @returns The room name if visible, undefined otherwise
 */
export function getRoomDisplay(
  room: string | null | undefined,
  roomPaidOnly: boolean,
  isPaid = false,
  canManage = false,
  isStudioManaged = false
): string | undefined {
  if (shouldShowRoom(room, roomPaidOnly, isPaid, canManage, isStudioManaged)) {
    return room ?? undefined;
  }
  return undefined;
}
